/**
 * AdjustModifier (Foundry: rule-element/adjust-modifier.ts): change a named modifier's value,
 * relabel it, change its damage type, or suppress it, on the statistics of one or more selectors.
 */
import type { ModifierAdjustment } from "../modifiers";
import { testPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import type { EngineState } from "../state";
import { isDamageType } from "../values";
import { AE_LIKE_MODES, CHANGE_MODE_DEFAULT_PRIORITIES, getNewValue, ChangeFailure, type AELikeMode } from "./ae-like";
import type { RuleHandler, RuleInstance } from "./base";

interface AdjustModifierData {
  mode: AELikeMode;
  selectors: string[];
  relabel: string | null;
  damageType: string | null;
  suppress: boolean;
  maxApplications: number;
  value: unknown;
  /** The modifier slug to adjust; null adjusts every modifier of the selector. */
  slug: string | null;
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  const suppress = s.suppress === true;
  const mode = suppress ? "override" : s.mode;
  if (!(AE_LIKE_MODES as readonly string[]).includes(String(mode))) return rule.fail(`mode: "${String(mode)}" is not a change mode`);
  const selectors = [
    ...(typeof s.selector === "string" && s.selector ? [s.selector] : []),
    ...(Array.isArray(s.selectors) ? s.selectors.filter((x): x is string => typeof x === "string" && !!x) : []),
  ];
  if (selectors.length === 0) return rule.fail("selectors: must have at least 1");
  const maxApplications = typeof s.maxApplications === "number" ? s.maxApplications : Infinity;
  if (suppress && typeof s.maxApplications === "number") return rule.fail("use of `maxApplications` in combination with `suppress` is not currently supported");
  const value = s.value === undefined || s.value === null ? null : cleanRuleValue(s.value);
  const damageType = typeof s.damageType === "string" && s.damageType ? s.damageType : null;
  if (!suppress && value === null && !damageType) return rule.fail("value: must be provided unless damageType is provided or suppress is true");
  const data: AdjustModifierData = {
    mode: mode as AELikeMode,
    selectors,
    relabel: typeof s.relabel === "string" && s.relabel ? s.relabel : null,
    damageType,
    suppress,
    maxApplications,
    value: value ?? null,
    slug: rule.slug,
  };
  rule.priority = typeof s.priority === "number" ? s.priority : CHANGE_MODE_DEFAULT_PRIORITIES[data.mode];
  rule.data = data as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as AdjustModifierData;
  let predicate: ReturnType<typeof resolveInjectedProperties<typeof rule.predicate>> | null = null;
  const adjustment: ModifierAdjustment = {
    slug: data.slug,
    test: (options): boolean => {
      predicate ??= resolveInjectedProperties(rule, state, structuredClone(rule.predicate), { warn: false });
      return testPredicate(predicate, new Set([...options, ...rule.item.getRollOptions("parent")]));
    },
    suppress: data.suppress,
    getNewValue: (current: number): number => {
      adjustment.applications ??= 0;
      if (data.value === null) return current;
      const change = resolveValue(rule, state, data.value);
      if (typeof change !== "number" || Number.isNaN(change)) {
        rule.fail("value: must resolve to a number");
        return current;
      }
      if (rule.ignored || adjustment.applications >= data.maxApplications) return current;
      adjustment.applications += 1;
      const next = getNewValue(data.mode, current, change);
      return next instanceof ChangeFailure ? current : Math.trunc(Number(next));
    },
    getDamageType: (current: string | null): string | null => {
      if (!data.damageType) return current;
      const damageType = resolveInjectedProperties(rule, state, data.damageType, { warn: false });
      if (!isDamageType(damageType)) {
        rule.fail(`${damageType} is an unrecognized damage type.`);
        return current;
      }
      return damageType;
    },
    source: rule.item.uuid,
    sourceName: rule.item.name,
  };
  if (data.relabel) {
    const relabel = resolveInjectedProperties(rule, state, data.relabel, { warn: false });
    adjustment.relabel = state.catalogue?.localize(relabel) ?? relabel;
  }
  for (const raw of data.selectors) {
    const selector = resolveInjectedProperties(rule, state, raw);
    if (selector === "null" || rule.ignored) continue;
    (state.synthetics.modifierAdjustments[selector] ??= []).push(adjustment);
  }
}

export const AdjustModifierHandler: RuleHandler = { key: "AdjustModifier", parse, beforePrepareData };
